/**
 * Client Configuration
 * Maps hostnames to their respective themes
 */

import kingsTheme from './themes/kingsTheme';
import galaxyTheme from './themes/galaxyTheme';
import ligamxTheme from './themes/ligamxTheme';
import lmbTheme from './themes/lmbTheme';
import aegTheme from './themes/aegTheme';

// Hostname to theme mapping
const hostnameToTheme = {
    // LA Kings
    'kings-wass.brandlytics.co': kingsTheme,

    // LA Galaxy
    'galaxy-wass.brandlytics.co': galaxyTheme,

    // Liga MX
    'ligamx-wass.brandlytics.co': ligamxTheme,

    // LMB (Liga Mexicana de Beisbol)
    'lmb-wass.brandlytics.co': lmbTheme,

    // AEG
    'aeg.theteam.brandlytics.co': aegTheme,

    // Development - defaults to Kings
    'localhost': kingsTheme,
    '127.0.0.1': kingsTheme,

    // Local development testing (add to /etc/hosts: 127.0.0.1 kings.local galaxy.local ligamx.local lmb.local)
    'kings.local': kingsTheme,
    'galaxy.local': galaxyTheme,
    'ligamx.local': ligamxTheme,
    'lmb.local': lmbTheme,
    'aeg.local': aegTheme,
};

// Product ID to theme mapping
const productIdToTheme = {
    '13': kingsTheme,
    '14': galaxyTheme,
    '15': ligamxTheme,
    '16': lmbTheme,
};

// Default theme when hostname doesn't match
const defaultTheme = kingsTheme;

/**
 * Get the theme configuration based on the selected product or hostname
 * Product selection takes priority over hostname
 * @returns {Object} The theme configuration object
 */
export function getTheme() {
    const productThemeId = localStorage.getItem('productThemeId');
    if (productThemeId && productIdToTheme[productThemeId]) {
        return productIdToTheme[productThemeId];
    }
    const hostname = window.location.hostname;
    return hostnameToTheme[hostname] || defaultTheme;
}

// Data channel names ('IN-VENUE', 'In Venue') -> theme key ('invenue')
const channelKey = channel => channel.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Get channel color by channel name
 * @param {string} channel - The channel name (e.g., 'Social', 'Digital', 'IN-VENUE')
 * @returns {string} The color value
 */
export function getChannelColor(channel) {
    const theme = getTheme();
    return theme.channels[channelKey(channel)]?.color || '#888888';
}

/**
 * Get channel gradient by channel name
 * @param {string} channel - The channel name
 * @returns {Array} Array of gradient colors [start, end]
 */
export function getChannelGradient(channel) {
    const theme = getTheme();
    return theme.channels[channelKey(channel)]?.gradient || ['#888888', '#666666'];
}

/**
 * Build channelToColor object for backward compatibility
 * @param {boolean} useAltLinear - Whether to use alternate linear color (for BarChart3D, ComparisonCard)
 * @returns {Object} Object mapping channel names to colors
 */
export function getChannelToColorMap(useAltLinear = false) {
    const theme = getTheme();
    return {
        Social: theme.channels.social.color,
        Digital: theme.channels.digital.color,
        Highlights: theme.channels.highlights.color,
        Linear: useAltLinear ? theme.channels.linear.colorAlt : theme.channels.linear.color,
        TV: theme.channels.tv.color,
        tv: theme.channels.tv.color,
        social: theme.channels.social.color,
        digital: theme.channels.digital.color,
        highlights: theme.channels.highlights.color,
        linear: useAltLinear ? theme.channels.linear.colorAlt : theme.channels.linear.color,
        'IN-VENUE': theme.channels.invenue.color,
        'In-Venue': theme.channels.invenue.color,
        'In-venue': theme.channels.invenue.color,
        'in-venue': theme.channels.invenue.color,
        invenue: theme.channels.invenue.color,
        OOH: theme.channels.ooh.color,
        Ooh: theme.channels.ooh.color,
        ooh: theme.channels.ooh.color,
    };
}

export { kingsTheme, galaxyTheme, ligamxTheme, lmbTheme, aegTheme };
export default getTheme;
